import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { FilePondStorageUpload } from "@apps/dashboard/components/FilePondStorageUpload";
import {
  createReceiptScan,
  fetchReceiptScans,
  homeExpenseQueryKeys,
} from "@apps/dashboard/lib/home-expense-api";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import {
  isReceiptExtractionErrorPayload,
  parseReceiptExtractedPayload,
} from "@project/core";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { Icon } from "@project/ui";

function scanStatusLabel(t: (k: string) => string, status: string): string {
  if (status === "pending") return t("dashboardUi.scan.statusPending");
  if (status === "processing") return t("dashboardUi.scan.statusProcessing");
  if (status === "completed") return t("dashboardUi.scan.statusCompleted");
  if (status === "failed") return t("dashboardUi.scan.statusFailed");
  return status;
}

export default function ScanPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const [pondResetKey, setPondResetKey] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  useSyncHouseFromParams(houseId);

  const scansQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.receiptScans(locale, houseId) : ["scans", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchReceiptScans(locale, houseId);
    },
    enabled: Boolean(houseId),
    refetchInterval: (q) => {
      const rows = q.state.data;
      if (!Array.isArray(rows) || rows.length === 0) return false;
      const active = rows.some((r) => r.status === "pending" || r.status === "processing");
      return active ? 2500 : false;
    },
  });

  const registerScanMutation = useMutation({
    mutationFn: async (imageStorageKey: string) => {
      if (!houseId) throw new Error("missing houseId");
      setErrorMsg(null);
      return createReceiptScan(locale, houseId, { imageStorageKey });
    },
    onSuccess: async () => {
      setPondResetKey((k) => k + 1);
      if (houseId) {
        await queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.receiptScans(locale, houseId) });
      }
    },
    onError: (e: unknown) => {
      setErrorMsg(e instanceof Error ? e.message : t("dashboardUi.states.errorHint"));
    },
  });

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  const busy = registerScanMutation.isPending;

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <div className="relative min-h-[70vh] overflow-hidden rounded-3xl bg-slate-900 text-on-primary">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800/90 to-slate-950" aria-hidden />
        <div className="relative z-10 mx-auto flex max-w-lg flex-col gap-8 px-6 py-10">
          <DashboardBackLink variant="onDark" to={dashboardPaths.home}>
            {t("dashboardUi.searchPage.backHome")}
          </DashboardBackLink>
          <div className="flex justify-center">
            <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 font-headline text-xs font-bold tracking-tight text-white">
              {busy ? t("dashboardUi.scan.registering") : t("dashboardUi.scan.overlayTitle")}
            </span>
          </div>

        <div className="flex flex-col items-center">
          <div className="relative h-96 w-72 overflow-hidden rounded-[2rem] border-[1.5px] border-white/30">
            <div className="absolute inset-2 rounded-3xl border border-secondary/40" aria-hidden />
            <div className="scanner-line absolute left-0 right-0 z-20" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
              <Icon name="ScanLine" className="size-12 text-secondary-container" strokeWidth={1.25} />
              <p className="font-body text-sm leading-relaxed text-white/80">{t("dashboardUi.scan.overlaySubtitle")}</p>
            </div>
          </div>

          <div className="mt-6 w-full max-w-sm rounded-2xl border border-white/15 bg-black/25 p-3 backdrop-blur-sm">
            <FilePondStorageUpload
              key={pondResetKey}
              locale={locale}
              mode="images"
              maxFiles={1}
              inputId="scan-receipt-filepond"
              disabled={busy}
              className="filepond-scan-upload w-full"
              onStoredKeys={(keys) => {
                const k = keys[keys.length - 1];
                if (k) void registerScanMutation.mutateAsync(k);
              }}
            />
          </div>
          {errorMsg ? <p className="mt-4 max-w-sm text-center text-sm text-error-container">{errorMsg}</p> : null}
        </div>

        <section className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
          <h2 className="font-headline text-sm font-bold tracking-wide text-white">{t("dashboardUi.scan.recentTitle")}</h2>
          {scansQuery.isLoading ? (
            <div className="mt-4">
              <LoadingState />
            </div>
          ) : scansQuery.isError ? (
            <div className="mt-4">
              <ErrorState onRetry={() => void scansQuery.refetch()} />
            </div>
          ) : (scansQuery.data ?? []).length === 0 ? (
            <p className="mt-3 text-body-md text-white/60">{t("dashboardUi.states.emptyHint")}</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {(scansQuery.data ?? []).map((s) => (
                <li
                  key={s.id}
                  className="rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white/90"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-xs text-white/60">{s.imageStorageKey}</span>
                    <span className="rounded-lg bg-white/10 px-2 py-0.5 text-label-md text-secondary-container">
                      {scanStatusLabel(t, s.status)}
                    </span>
                  </div>
                  {s.status === "failed" &&
                  s.extractedPayload &&
                  isReceiptExtractionErrorPayload(s.extractedPayload) ? (
                    <p className="mt-2 text-body-md text-error-container">
                      {t(s.extractedPayload.extractionError.key)}
                    </p>
                  ) : null}
                  {s.status === "completed" &&
                  s.extractedPayload &&
                  !isReceiptExtractionErrorPayload(s.extractedPayload) ? (
                    <div className="mt-2 space-y-3 rounded-lg bg-black/30 p-3">
                      <p className="text-label-md text-white/50">{t("dashboardUi.scan.extractedTitle")}</p>
                      {(() => {
                        const p = parseReceiptExtractedPayload(s.extractedPayload);
                        if (!p) {
                          return (
                            <pre className="max-h-32 overflow-auto text-[10px] leading-relaxed text-white/70">
                              {JSON.stringify(s.extractedPayload, null, 2)}
                            </pre>
                          );
                        }
                        const ccy = (p.currency ?? "EUR").toUpperCase();
                        return (
                          <dl className="grid gap-1.5 text-body-md text-white/85">
                            {p.merchant ? (
                              <div className="flex flex-wrap gap-2">
                                <dt className="text-white/50">{t("dashboardUi.scan.extractedMerchant")}</dt>
                                <dd>{p.merchant}</dd>
                              </div>
                            ) : null}
                            {p.occurredOn ? (
                              <div className="flex flex-wrap gap-2">
                                <dt className="text-white/50">{t("dashboardUi.scan.extractedDate")}</dt>
                                <dd>{p.occurredOn}</dd>
                              </div>
                            ) : null}
                            {typeof p.totalAmountMinor === "number" ? (
                              <div className="flex flex-wrap gap-2">
                                <dt className="text-white/50">{t("dashboardUi.scan.extractedTotal")}</dt>
                                <dd>{formatMinorAsCurrency(p.totalAmountMinor, ccy, locale)}</dd>
                              </div>
                            ) : null}
                          </dl>
                        );
                      })()}
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full border-white/20 bg-white/10 text-white hover:bg-white/20"
                        asChild
                      >
                        <Link to={`${dashboardPaths.houseExpenseNew(houseId)}?fromScan=${s.id}`}>
                          {t("dashboardUi.scan.createExpenseFromScan")}
                        </Link>
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-[10px] uppercase tracking-wider text-white/40">{t("dashboardUi.scan.linkExpenseHint")}</p>
        </section>
        </div>
      </div>
    </div>
  );
}
